import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Play, Clock } from 'lucide-react';

export default function EpisodesList() {
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchEpisodes();
  }, []);

  const fetchEpisodes = async () => {
    try {
      const response = await fetch('/api/getEpisodes');
      if (!response.ok) {
        throw new Error('Failed to fetch episodes');
      }
      const data = await response.json();
      setEpisodes(data.episodes);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const formatDuration = (ms) => {
    const minutes = Math.floor(ms / 60000);
    return `${minutes} min`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const truncateDescription = (text, maxLength = 150) => {
    if (!text) return '';
    const plainText = text.replace(/<[^>]*>/g, ''); // Remove HTML tags
    if (plainText.length <= maxLength) return plainText;
    return plainText.substring(0, maxLength) + '...';
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        <p className="mt-4 text-slate-600">Loading episodes...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Error loading episodes: {error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-slate-800 mb-2">Latest Episodes</h2>
        <p className="text-slate-600">Check out our newest conversations</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {episodes.map((episode) => (
          <Card
            key={episode.id}
            className="group cursor-pointer border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white rounded-2xl overflow-hidden"
            onClick={() => window.open(episode.external_url, '_blank')}
          >
            <CardContent className="p-0">
              {episode.image && (
                <div className="relative overflow-hidden">
                  <img
                    src={episode.image}
                    alt={episode.name}
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="flex items-center gap-2 text-white/90 text-sm">
                      <Clock className="w-4 h-4" />
                      <span>{formatDuration(episode.duration_ms)}</span>
                      <span className="mx-2">•</span>
                      <span>{formatDate(episode.release_date)}</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="p-6">
                <h3 className="font-bold text-lg text-slate-800 mb-3 line-clamp-2 group-hover:text-green-600 transition-colors">
                  {episode.name}
                </h3>

                <p className="text-slate-600 text-sm mb-4 line-clamp-3">
                  {truncateDescription(episode.description)}
                </p>

                <Button
                  variant="secondary"
                  className="bg-green-600 text-white hover:bg-green-700 font-semibold px-6 py-2 rounded-xl shadow-md group-hover:shadow-lg transition-all duration-300 w-full flex items-center justify-center gap-2"
                >
                  <Play className="w-4 h-4" />
                  Listen on Spotify
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
